import { DEFAULT_LOCALE, type Locale, matchSupportedLocale } from "@adpilot/shared";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { i18n } from "@/i18n";

/** 浏览器语言检测：按 navigator.languages 顺序匹配，无法匹配时回落 DEFAULT_LOCALE */
export function detectBrowserLocale(): Locale {
  if (typeof navigator === "undefined") return DEFAULT_LOCALE;
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of candidates) {
    const matched = matchSupportedLocale(tag);
    if (matched) return matched;
  }
  return DEFAULT_LOCALE;
}

interface LocaleState {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

/**
 * 语言偏好独立于 auth store 持久化：登录前（/login）也需要生效，
 * 且 logout 清空 auth 状态时语言不应丢失。
 * 服务端偏好（users.locale）在登录后由 auth 流程写回本 store。
 */
export const useLocaleStore = create<LocaleState>()(
  persist(
    (set) => ({
      locale: detectBrowserLocale(),
      setLocale: (locale) => set({ locale }),
    }),
    { name: "adpilot-locale" },
  ),
);

function applyLocale(locale: Locale) {
  if (i18n.language !== locale) {
    void i18n.changeLanguage(locale);
  }
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

// persist 对 localStorage 是同步 rehydrate，此处拿到的已是持久化后的值
applyLocale(useLocaleStore.getState().locale);
useLocaleStore.subscribe((state) => applyLocale(state.locale));
